/**
 * File Name          : navbar-layout.component.ts
 * Author             : Jared Laine Dyreson
 * Version            : 0.1.0
 * Date               : 2026-03-18
 * Description        : Short Description (manual edit required)
 *
 * Copyright (C) Innovative Design Labs, Inc. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * This file is subject to the terms and conditions defined in
 * file 'IDL_PROJECTTECH_LICENSE.txt', which is part of this source code package.
 */

import { Component, OnDestroy } from '@angular/core';
import { SensorColorComponent } from '../../components/icons/sensor-color/sensor-color.component';
import { SensorBatteryComponent } from '../../components/icons/sensor-battery/sensor-battery.component';
import { DxButtonModule, DxPopupModule } from 'devextreme-angular';
import {
  ActivatedRoute,
  Router,
  RouterOutlet,
  NavigationEnd,
} from '@angular/router';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  DatasnapUnit,
  DatasnapLevel,
  DatasnapLesson,
  UNITS,
} from '../../models/lessons.model';
import { DatasnapDevice } from '../../models/datasnap-device.model';
import { DatasnapDeviceService } from '../../services/datasnap-device.service';
import { confirm, alert } from 'devextreme/ui/dialog';
import { CommonModule } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { BlinkPatternHelpComponent } from '../../components/help/blink-pattern-help/blink-pattern-help.component';
import { DeviceSettingsComponent } from '../../components/device-settings/device-settings.component';
import { Device_State } from '../../../../projects/idl-physens/src/lib/models/physens-devices/physens-device.model';
import { VersionService } from '../../services/version.service';
import { ScreenserviceService } from '../../services/screenservice.service';

@Component({
  selector: 'app-navbar-layout',
  standalone: true,
  imports: [
    SensorColorComponent,
    SensorBatteryComponent,
    DxButtonModule,
    DxPopupModule,
    RouterOutlet,
    CommonModule,
    BlinkPatternHelpComponent,
    DeviceSettingsComponent,
  ],
  templateUrl: './navbar-layout.component.html',
  styleUrl: './navbar-layout.component.scss',
})
export class NavbarLayoutComponent implements OnDestroy {
  unitId?: string;
  unit?: DatasnapUnit | undefined;
  levelId?: string;
  level?: DatasnapLevel | undefined;
  lessonId?: string;
  lesson?: DatasnapLesson | undefined;

  devices: DatasnapDevice[] = [];

  showHelpPopup = false;
  showDeviceSettingsPopup = false;
  showAboutPopup = false;

  Device_State = Device_State;
  appVersion: string = '';

  private devicesSub?: Subscription;
  constructor(
    private route: ActivatedRoute,
    private datasnapDeviceService: DatasnapDeviceService,
    private router: Router,
    private versionService: VersionService,
    public screenService: ScreenserviceService,
  ) {
    this.versionService.getVersion().then((version) => {
      this.appVersion = version;
    });
  }

  ngOnInit() {
    // Subscribe to router events to detect navigation changes
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateFromRoute();
      });

    // Initial load
    this.updateFromRoute();
  }

  private updateFromRoute() {
    // Use routerState.snapshot.root so params are always fresh, even when
    // AppRouteReuseStrategy restores a cached component (ActivatedRoute.snapshot
    // is stale after a detach/attach cycle).
    let snap: ActivatedRouteSnapshot | null =
      this.router.routerState.snapshot.root;
    const params: Record<string, string> = {};
    while (snap) {
      Object.assign(params, snap.params);
      snap = snap.firstChild;
    }

    this.unitId = params['unitId'];
    this.unit = UNITS.find((l) => l.id === this.unitId);

    this.levelId = params['levelId'];
    this.level = this.unit?.levels.find((l) => l.id === this.levelId);

    this.lessonId = params['lessonId'];
    this.lesson = this.level?.lessons.find((l) => l.id === this.lessonId);

    if (this.unit && this.level && this.lesson) {
      if (!this.devicesSub) {
        this.devicesSub = this.datasnapDeviceService.devices$.subscribe(
          (devices) => {
            console.log('devices changed:', devices.length);
            this.devices = devices;
          },
        );
      }
    }
  }

  ngOnDestroy() {
    this.devicesSub?.unsubscribe();
  }
  checkGoHome = () => {
    if (this.devices.length === 0) {
      this.router.navigate(['home']);
      return;
    }

    confirm(
      'Are you sure you want to leave the lesson? All data will be lost',
      'Home',
    )
      .then((dialogResult) => {
        if (dialogResult) {
          this.datasnapDeviceService.removeAllDevices();
          this.router.navigate(['home']);
        }
      })
      .catch((error) => {
        console.error('Error showing confirmation dialog:', error);
      });
  };

  showHelpWindow = () => {
    this.showHelpPopup = true;
  };

  getUnitIconPath(unit: DatasnapUnit | undefined): string {
    if (!unit) {
      return '';
    }
    switch (unit.id) {
      case 'impact':
        return '/assets/Unit_Icons/Impact_Icon.svg';
      case 'air_racer':
        return '/assets/Unit_Icons/AirRacer_Icon.svg';
      case 'projectile':
        return '/assets/Unit_Icons/Projectile_Icon.svg';
      default:
        return '';
    }
  }

  removeDevice = (device: DatasnapDevice) => {
    confirm(
      'Are you sure you want to disconnect your device? All progress will be lost',
      'Disconnect Device',
    )
      .then((dialogResult) => {
        if (dialogResult) {
          this.datasnapDeviceService.removeDevice(device);
        }
      })
      .catch((error) => {
        console.error('Error showing confirmation dialog:', error);
      });
  };

  showDeviceSettings = (device: DatasnapDevice) => {
    this.showDeviceSettingsPopup = true;
  };

  notifyDevice = (device: DatasnapDevice) => {
    return () => {
      device.notify();
    };
  };

  getCurrentYear(): number {
    return new Date().getFullYear();
  }

  openPrivacyPolicy = () => {
    window.open('https://crexo.com/privacy/', '_blank');
  };

  openProductSupport = () => {
    window.open('https://pages.crexo.com/request-support', '_blank');
  };
}
